import React, { useState, useEffect } from 'react';
import { Trash2, Layers, Download, Search, Filter, Hash, FileText, ChevronRight, X, Star, AlertTriangle, Settings, Tag, Folder, Image as ImageIcon, Video, Globe } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

export default function CorpusRepository() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activeTab, setActiveTab] = useState('TEXT');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isSensitiveFilter, setIsSensitiveFilter] = useState('');
  const [auditStatusFilter, setAuditStatusFilter] = useState('APPROVED');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  
  // Sensitive Words Config
  const [configOpen, setConfigOpen] = useState(false);
  const [sensitiveWords, setSensitiveWords] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);

  // Viewer Modal
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Edit State
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState('');
  const [savingItem, setSavingItem] = useState(false);

  const fetchData = () => {
    fetchApprovedItems();
    fetchConfig();
  };

  useEffect(() => {
    fetchData();
    window.addEventListener('globalFilterChange', fetchData);
    return () => window.removeEventListener('globalFilterChange', fetchData);
  }, []);

  const fetchConfig = async () => {
    const projectId = localStorage.getItem('globalBrand');
    if (!projectId || projectId === 'all' || projectId === 'undefined') return;
    try {
      const res = await axios.get(`/api/corpus/config/${projectId}`);
      setSensitiveWords(res.data.sensitiveWordsConfig || '');
    } catch(e) {
      console.error(e);
    }
  };

  const saveConfig = async () => {
    const projectId = localStorage.getItem('globalBrand');
    if (!projectId || projectId === 'all' || projectId === 'undefined') return;
    try {
      setSavingConfig(true);
      await axios.post(`/api/corpus/config/${projectId}`, { sensitiveWordsConfig: sensitiveWords });
      setConfigOpen(false);
      fetchApprovedItems(); // Re-fetch to apply new highlights if backend re-processed (optional, but good for sync)
    } catch(e) {
      alert('保存失败');
    } finally {
      setSavingConfig(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, auditStatusFilter, categoryFilter, isSensitiveFilter]);

  const fetchApprovedItems = async () => {
    const projectId = localStorage.getItem('globalBrand');
    if (!projectId || projectId === 'all' || projectId === 'undefined') {
      setItems([]);
      setTotalItems(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const sourceTypes = activeTab === 'MEDIA' ? 'IMAGE,VIDEO,MEDIA' : 'TEXT,LINK,DOCUMENT,DOC,FILE,EXCEL,ECO_LINK';
      let url = `/api/corpus/items?projectId=${projectId}&page=${currentPage}&pageSize=${pageSize}&sourceTypes=${sourceTypes}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (auditStatusFilter) url += `&status=${auditStatusFilter}`;
      if (categoryFilter) url += `&category=${encodeURIComponent(categoryFilter)}`;
      if (isSensitiveFilter) url += `&isSensitive=${isSensitiveFilter}`;

      const res = await axios.get(url);
      setItems(res.data.items || []);
      setTotalItems(res.data.pagination?.total || res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch corpus items:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleExport = () => {
    if (items.length === 0) {
      alert('当前没有可导出的语料数据');
      return;
    }
    
    // CSV Header (UTF-8 BOM for Excel)
    let csvContent = '\uFEFF'; 
    csvContent += '语料ID,来源类型,标题,质量得分,核心摘要,业务场景/意图,核心卖点,关键词汇,入库时间\n';
    
    items.forEach(item => {
      const data = parseStructuredData(item.structuredData);
      
      const id = item.id || '';
      const type = item.sourceType || '';
      const title = (item.title || '').replace(/"/g, '""').replace(/\n/g, ' ');
      const score = data.qualityScore || item.qualityScore || 0;
      const summary = (data.summary || '').replace(/"/g, '""').replace(/\n/g, ' ');
      const intents = (data.intents || []).join('；').replace(/"/g, '""').replace(/\n/g, ' ');
      const sellingPoints = (data.sellingPoints || []).join('；').replace(/"/g, '""').replace(/\n/g, ' ');
      const keywords = (data.keywords || []).join('；').replace(/"/g, '""').replace(/\n/g, ' ');
      const date = new Date(item.updatedAt).toLocaleDateString();

      csvContent += `"${id}","${type}","${title}","${score}","${summary}","${intents}","${sellingPoints}","${keywords}","${date}"\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `品牌结构化知识图谱_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchApprovedItems();
  }, [currentPage, activeTab, searchQuery, auditStatusFilter, categoryFilter, isSensitiveFilter]);

  const parseStructuredData = (jsonStr) => {
    try {
      if (!jsonStr) return {};
      return JSON.parse(jsonStr);
    } catch (e) {
      return {};
    }
  };

  const highlightText = (text, matchedWords) => {
    if (!text) return '';
    if (!matchedWords || matchedWords.length === 0) return text;
    
    // Sort words by length descending so longer words match first
    const words = [...matchedWords].sort((a, b) => b.length - a.length);
    const regex = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
    
    const parts = text.split(regex);
    return parts.map((part, i) => {
      if (words.some(w => w.toLowerCase() === part.toLowerCase())) {
        return <mark key={i} className="bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-300 font-bold px-0.5 rounded">{part}</mark>;
      }
      return part;
    });
  };

  const filteredItems = items;
  const totalPages = Math.ceil(totalItems / pageSize);
  
  // Actually we should let the backend extract categories, but for now we fallback to standard static list or fetched list if possible.
  // We'll keep a minimal list of categories for filter or rely on existing ones.
  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];

  const openViewer = (item) => {
    setSelectedItem(item);
    setEditCategory(item.category || '');
    setEditTags(item.tags || '');
    setViewerOpen(true);
  };

  const handleSaveItem = async () => {
    try {
      setSavingItem(true);
      const res = await axios.patch(`/api/corpus/items/${selectedItem.id}`, {
        category: editCategory,
        tags: editTags
      });
      setItems(items.map(i => i.id === selectedItem.id ? { ...i, category: editCategory, tags: editTags } : i));
      setSelectedItem(res.data);
    } catch(err) {
      alert('保存失败');
    } finally {
      setSavingItem(false);
    }
  };

  
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`确定要删除选中的 ${selectedIds.length} 项资产吗？\n(注意：对于从生态溯源里采纳的文章，彻底删除后才能在溯源列表里恢复为“未采纳”状态。\n点击【确定】直接彻底删除，此操作不可恢复)`)) return;
    
    try {
      await axios.delete('/api/corpus/items/batch', { data: { ids: selectedIds } });
      setItems(items.filter(i => !selectedIds.includes(i.id)));
      setSelectedIds([]);
      alert('批量删除成功');
    } catch(err) {
      console.error(err);
      alert('批量删除失败');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredItems.length && filteredItems.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredItems.map(i => i.id));
    }
  };

  const handleRemove = async (item) => {
    if (!confirm('确定将该记录退回吗？\n注意：对于从生态溯源里采纳的文章，彻底删除后才能在溯源列表里恢复为“未采纳”状态。\n点击【确定】直接彻底删除并取消采纳。')) return;
    try {
      // 彻底删除该条语料，这样 CorpusCrawl 里的 adoptedIds 才会解绑，状态才能真正回退
      await axios.delete(`/api/corpus/items?id=${item.id}`);
      setItems(items.filter(i => i.id !== item.id));
      setViewerOpen(false);
    } catch(err) {
      console.error(err);
      alert('操作失败');
    }
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex justify-between items-center mb-6 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Layers className="w-6 h-6 text-purple-500" />
            结构化语料库
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            已被 AI 拆解提炼并入库的高质量语料资产。支持敏感词高亮预警与多维分类标签。
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setConfigOpen(true)}
            className="bg-red-50 text-red-600 border border-red-200 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400 hover:bg-red-100 transition-colors px-4 py-2 rounded-lg flex items-center gap-2 shadow-sm">
            <AlertTriangle className="w-4 h-4" /> 预警规则设置
          </button>
          <button onClick={handleExport} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm">
            <Download className="w-4 h-4" /> 导出知识图谱
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-slate-700 mb-6 shrink-0">
        <button 
          onClick={() => { setActiveTab('TEXT'); setSelectedIds([]); }}
          className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'TEXT' ? 'border-purple-500 text-purple-600 dark:text-purple-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}>
          <FileText className="w-4 h-4 inline-block mr-2" />
          文本与文档语料
        </button>
        <button 
          onClick={() => { setActiveTab('MEDIA'); setSelectedIds([]); }}
          className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'TEXT' ? 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300' : 'border-purple-500 text-purple-600 dark:text-purple-400'}`}>
          <ImageIcon className="w-4 h-4 inline-block mr-2" />
          多媒体视觉资产
        </button>
      </div>

      <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6">
        <div className="flex flex-wrap items-center gap-4 mb-6 shrink-0">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="在入库语料库中全文检索 (按回车搜索)..." 
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setSearchQuery(searchInput); }}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 dark:text-gray-100"
            />
          </div>
          
          <select 
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="py-2 pl-3 pr-8 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">全部分类</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <select
            value={auditStatusFilter}
            onChange={(e) => setAuditStatusFilter(e.target.value)}
            className="py-2 pl-3 pr-8 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="APPROVED">已过审 (正式入库)</option>
            <option value="PENDING">待审核 (AI提炼完成)</option>
          </select>

          <select 
            value={isSensitiveFilter}
            onChange={(e) => setIsSensitiveFilter(e.target.value)}
            className="py-2 pl-3 pr-8 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">全部安全状态</option>
            <option value="true">⚠️ 高危预警</option>
            <option value="false">✅ 安全内容</option>
          </select>

          <div className="text-sm text-gray-500 dark:text-gray-400 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-4 py-2 rounded-lg border border-purple-100 dark:border-purple-800/50">
            资产规模: <span className="font-bold">{totalItems}</span> 篇
          </div>
          <button onClick={toggleSelectAll} className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium">
            {selectedIds.length === filteredItems.length && filteredItems.length > 0 ? '取消全选' : '全选本页'}
          </button>
          {selectedIds.length > 0 && (
            <button onClick={handleBatchDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center text-sm font-medium">
              <Trash2 className="w-4 h-4 mr-2" />
              批量删除 ({selectedIds.length})
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center flex-1">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex-1 flex flex-col justify-center items-center text-center">
            <Layers className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-slate-400 mb-2">暂无匹配的结构化语料。</p>
            <p className="text-sm text-gray-400">请前往“基础语料池”或“生态溯源”导入并审核内容。</p>
          </div>
        ) : activeTab === 'MEDIA' ? (
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredItems.map(item => (
                <div 
                  key={item.id} 
                  className={`bg-white dark:bg-slate-800 rounded-xl border overflow-hidden hover:shadow-lg transition-all relative group flex flex-col ${selectedIds.includes(item.id) ? "border-purple-500 ring-2 ring-purple-200" : "border-gray-200 dark:border-slate-700"}`}
                >
                  <div className="absolute top-2 left-2 z-10">
                    <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelect(item.id)} className="w-5 h-5 rounded text-purple-600 focus:ring-purple-500 bg-white/80 border-gray-300 cursor-pointer shadow-sm" />
                  </div>
                  {item.sourceType === 'IMAGE' ? (
                    <img src={item.sourceUrl} alt={item.title} className="w-full aspect-video object-cover block bg-gray-100 dark:bg-slate-900 relative z-0" onError={(e) => { e.target.onerror = null; e.target.src = "/assets/fallback-image.png"; }} style={{ WebkitTransform: "translateZ(0)", transform: "translateZ(0)" }} />
                  ) : item.sourceType === 'VIDEO' ? (
                    <video src={item.sourceUrl} controls className="w-full aspect-video object-cover block bg-gray-100 dark:bg-slate-900"  />
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center bg-gray-100 text-gray-400">不支持的媒体格式</div>
                  )}
                  <div 
                    className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-4 cursor-pointer translate-y-full group-hover:translate-y-0 transition-transform duration-300 flex flex-col justify-end"
                    onClick={() => setPreviewMedia(item)}
                  >
                     <p className="text-white text-sm font-medium line-clamp-2">{item.sourceType === 'IMAGE' ? '图片视觉' : '视频资产'}</p>
                     <div className="flex justify-between items-center mt-3">
                       <span className="text-gray-300 text-xs">{new Date(item.createdAt).toLocaleDateString()}</span>
                       
                        <>
                        {item.status === 'PENDING' && (user?.role === 'ADMIN' || user?.permissions?.includes('corpus:audit')) && (
                          <button onClick={async (e) => { 
                           e.stopPropagation(); 
                           try {
                             await axios.patch(`/api/corpus/items/${item.id}`, { status: 'APPROVED' });
                             setItems(items.filter(i => i.id !== item.id));
                           } catch(err) {
                             alert('操作失败');
                           }
                          }} className="px-2 py-1 mr-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs transition-colors">过审</button>
                        )}
                        {item.status !== 'REJECTED' && (user?.role === 'ADMIN' || user?.permissions?.includes('corpus:audit')) && (
                          <button onClick={async (e) => { 
                           e.stopPropagation(); 
                           if(!confirm('确定驳回此多媒体资产吗？')) return;
                           try {
                             await axios.patch(`/api/corpus/items/${item.id}`, { status: 'REJECTED' });
                             setItems(items.filter(i => i.id !== item.id));
                           } catch(err) {
                             alert('驳回失败');
                           }
                          }} className="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-xs transition-colors">驳回</button>
                        )}
                        </>

                     </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {filteredItems.map(item => {
                const aiData = parseStructuredData(item.structuredData);
                const score = item.qualityScore || aiData.qualityScore || 0;
                const intents = aiData.intents || [];
                const keywords = aiData.keywords || [];
                const summary = aiData.summary || "未生成摘要";
                let matchedWords = [];
                try { if(item.matchedSensitiveWords) matchedWords = JSON.parse(item.matchedSensitiveWords); } catch(e){}

                return (
                  <div 
                    key={item.id} 
                    onClick={(e) => { if (e.target.type !== 'checkbox') openViewer(item); }}
                    className={`bg-white dark:bg-slate-800 rounded-xl border overflow-hidden hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md transition-all cursor-pointer flex flex-col group relative ${selectedIds.includes(item.id) ? 'border-purple-500 ring-2 ring-purple-100' : 'border-gray-200 dark:border-slate-700'}`}
                  >
                    {item.isSensitive && (
                      <div className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg rounded-tr-xl flex items-center gap-1 shadow-sm">
                        <AlertTriangle className="w-3 h-3" /> 敏感预警
                      </div>
                    )}

                    <div className="p-5 flex-1 flex flex-col">
                      <div className="absolute top-4 left-4 z-10">
                        <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelect(item.id)} onClick={e => e.stopPropagation()} className="w-5 h-5 rounded text-purple-600 focus:ring-purple-500 border-gray-300 cursor-pointer" />
                      </div>
                      <div className="flex justify-between items-start mb-2 gap-4">
                        <h3 className="font-bold text-gray-900 dark:text-white text-base line-clamp-2 leading-snug group-hover:text-purple-600 transition-colors flex-1 pl-8 pr-16" title={item.title}>
                          {highlightText(item.title, matchedWords)}
                        </h3>
                      </div>
                      
                      {/* 分类和标签简显 */}
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        {item.category && (
                           <span className="flex items-center gap-1 text-[11px] bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded font-medium">
                             <Folder className="w-3 h-3" /> {item.category}
                           </span>
                        )}
                        {item.tags && item.tags.split(',').map((t, i) => (
                           <span key={i} className="flex items-center gap-1 text-[11px] bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300 px-1.5 py-0.5 rounded font-medium border border-indigo-100 dark:border-indigo-800/50">
                             <Tag className="w-3 h-3" /> {t.trim()}
                           </span>
                        ))}
                        {score > 0 && (
                          <div className={`shrink-0 px-1.5 py-0.5 rounded text-[11px] font-bold flex items-center gap-1 ${
                            score >= 80 ? 'bg-green-100 text-green-700' :
                            score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            <Star className="w-3 h-3 fill-current" />
                            {score}
                          </div>
                        )}
                      </div>

                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 bg-gray-50 dark:bg-slate-900/50 p-2.5 rounded border border-gray-100 dark:border-slate-700/50">
                        {summary}
                      </p>
                      
                      <div className="flex flex-col gap-2 mt-auto">
                        {intents.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-medium text-purple-600 dark:text-purple-400 shrink-0 mt-0.5 w-10">意图:</span>
                            <div className="flex flex-wrap gap-1.5">
                              {intents.slice(0, 3).map((intent, i) => (
                                <span key={i} className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded truncate max-w-[120px]">
                                  {intent}
                                </span>
                              ))}
                              {intents.length > 3 && <span className="text-xs text-gray-400">+{intents.length-3}</span>}
                            </div>
                          </div>
                        )}
                        {keywords.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 shrink-0 mt-0.5 w-10">词包:</span>
                            <div className="flex flex-wrap gap-1.5">
                              {keywords.slice(0, 4).map((kw, i) => (
                                <span key={i} className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded truncate max-w-[100px]">
                                  {kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="px-5 py-2.5 border-t border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-900/50 flex justify-between items-center text-xs text-gray-500">
                      <span className="flex items-center gap-1.5 font-medium">
                        {item.sourceType === 'ECO_LINK' ? <Globe className="w-3.5 h-3.5 text-blue-400"/> : <FileText className="w-3.5 h-3.5 text-gray-400"/>} 
                        {item.sourceType === 'ECO_LINK' ? '外部溯源 (LINK)' : item.sourceType}
                      </span>
                      <span>入库时间: {new Date(item.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detail Viewer Modal */}
      {previewMedia && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4" onClick={() => setPreviewMedia(null)}>
          <div className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setPreviewMedia(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 p-2"
            >
              <X className="w-8 h-8" />
            </button>
            {previewMedia.sourceType === 'IMAGE' ? (
              <img src={previewMedia.sourceUrl} alt="Preview" className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            ) : (
              <video src={previewMedia.sourceUrl} controls autoPlay className="max-w-full max-h-[85vh] rounded-lg shadow-2xl" />
            )}
          </div>
        </div>
      )}

      {viewerOpen && selectedItem && (() => {
        const aiData = parseStructuredData(selectedItem.structuredData);
        let matchedWords = [];
        try { if(selectedItem.matchedSensitiveWords) matchedWords = JSON.parse(selectedItem.matchedSensitiveWords); } catch(e){}

        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex justify-between items-start p-5 border-b border-gray-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
              <div className="pr-8">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 px-2 py-0.5 rounded text-xs font-bold border border-purple-200 dark:border-purple-800">入库编号 #{selectedItem.id}</span>
                  <span className="text-xs text-gray-500">类型: {selectedItem.sourceType}</span>
                  {selectedItem.isSensitive && (
                    <span className="bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 px-2 py-0.5 rounded text-xs font-bold border border-red-200 dark:border-red-800 flex items-center gap-1">
                       <AlertTriangle className="w-3 h-3" /> 命中敏感词 ({matchedWords.length})
                    </span>
                  )}
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-snug">{highlightText(selectedItem.title, matchedWords)}</h2>
              </div>
              <button onClick={() => setViewerOpen(false)} className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-full p-1.5 transition-colors shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-0 flex flex-col md:flex-row">
              {/* Left Column: AI Structured Data & Edit */}
              <div className="w-full md:w-4/12 border-r border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 flex flex-col gap-6 overflow-y-auto">
                
                {/* 手动编辑分类和标签 */}
                <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                   <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                     <Settings className="w-4 h-4 text-indigo-500" />
                     资产分类与标签
                   </h3>
                   <div className="space-y-3">
                     <div>
                       <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">所属分类树 (Category)</label>
                       <input 
                         type="text" 
                         value={editCategory}
                         onChange={e => setEditCategory(e.target.value)}
                         placeholder="例如：品牌宣发 / 竞品追踪"
                         className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                       />
                     </div>
                     <div>
                       <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">多维标签组 (Tags) - 逗号分隔</label>
                       <input 
                         type="text" 
                         value={editTags}
                         onChange={e => setEditTags(e.target.value)}
                         placeholder="例如：续航测试, 负面新闻, 提车分享"
                         className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                       />
                     </div>
                    {(user?.role === 'ADMIN' || user?.permissions?.includes('corpus:edit')) && (
                     <button 
                        onClick={handleSaveItem}
                        disabled={savingItem}
                        className="w-full bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 dark:text-indigo-400 py-1.5 rounded text-sm font-medium transition-colors"
                      >
                        {savingItem ? '保存中...' : '保存分类信息'}
                     </button>
                    )}
                   </div>
                </div>

                <div>
                  <h3 className="flex items-center gap-2 font-bold text-gray-900 dark:text-white mb-3">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    商业价值评分: {selectedItem.qualityScore || aiData.qualityScore || 0}
                  </h3>
                  <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-2.5">
                    <div className="bg-yellow-400 h-2.5 rounded-full" style={{ width: `${selectedItem.qualityScore || aiData.qualityScore || 0}%` }}></div>
                  </div>
                </div>

                {aiData.summary && (
                  <div>
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-2 text-purple-600">核心摘要</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed bg-purple-50/50 dark:bg-purple-900/10 p-3 rounded-lg border border-purple-100 dark:border-purple-800/30">
                      {aiData.summary}
                    </p>
                  </div>
                )}

                {aiData.sellingPoints && aiData.sellingPoints.length > 0 && (
                  <div>
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-2 text-green-600">提取卖点 (Selling Points)</h3>
                    <ul className="space-y-2">
                      {aiData.sellingPoints.map((sp, idx) => (
                        <li key={idx} className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2 bg-green-50/50 dark:bg-green-900/10 p-2.5 rounded-lg border border-green-100 dark:border-green-800/30">
                          <span className="text-green-500 font-bold mt-0.5">•</span>
                          <span className="leading-relaxed">{sp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {aiData.intents && aiData.intents.length > 0 && (
                  <div>
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-2 text-orange-600">消费者意图 (Intents)</h3>
                    <div className="flex flex-wrap gap-2">
                      {aiData.intents.map((intent, idx) => (
                        <span key={idx} className="text-sm px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800/50 rounded-full">
                          {intent}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {aiData.keywords && aiData.keywords.length > 0 && (
                  <div>
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-2 text-blue-600">标准核心词 (Keywords)</h3>
                    <div className="flex flex-wrap gap-2">
                      {aiData.keywords.map((kw, idx) => (
                        <span key={idx} className="text-sm px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50 rounded">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Raw Content */}
              <div className="w-full md:w-8/12 bg-gray-50 dark:bg-slate-900 p-6 overflow-y-auto">
                <h3 className="font-bold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> 原始凭证 (生肉)
                  {matchedWords.length > 0 && (
                    <span className="ml-auto text-xs font-normal text-red-500 bg-red-100 dark:bg-red-900/30 px-2 py-0.5 rounded-full">
                      高亮标记命中敏感词汇
                    </span>
                  )}
                </h3>
                <div className="whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-400 font-mono leading-loose bg-white dark:bg-slate-800 p-5 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm">
                  {highlightText(selectedItem.rawContent, matchedWords)}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 flex justify-between items-center">
              <span className="text-xs text-gray-400">入库时间: {new Date(selectedItem.updatedAt).toLocaleString()}</span>
              <div className="flex gap-3">
                {selectedItem.status === 'PENDING' && (user?.role === 'ADMIN' || user?.permissions?.includes('corpus:audit')) && (
                    <button 
                      onClick={async () => {
                        try {
                          await axios.patch(`/api/corpus/items/${selectedItem.id}`, { status: 'APPROVED' });
                          setItems(items.filter(i => i.id !== selectedItem.id));
                          setViewerOpen(false);
                        } catch(e) {
                          alert('审核入库失败: ' + (e.response?.data?.error || e.message));
                        }
                      }}
                      className="px-4 py-2 text-sm text-green-600 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/40 rounded-lg font-medium transition-colors border border-green-200 dark:border-green-800"
                    >
                      通过审核 (正式入库)
                    </button>
                )}
                {selectedItem.status !== 'REJECTED' && (user?.role === 'ADMIN' || user?.permissions?.includes('corpus:audit')) && (
                    <button 
                      onClick={async () => {
                        try {
                          await axios.patch(`/api/corpus/items/${selectedItem.id}`, { status: 'REJECTED' });
                          setItems(items.filter(i => i.id !== selectedItem.id));
                          setViewerOpen(false);
                        } catch(e) {
                          alert('驳回失败: ' + (e.response?.data?.error || e.message));
                        }
                      }}
                      className="px-4 py-2 text-sm text-yellow-600 bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/40 rounded-lg font-medium transition-colors border border-yellow-200 dark:border-yellow-800"
                    >
                      驳回提炼
                    </button>
                )}
                {(user?.role === 'ADMIN' || user?.permissions?.includes('corpus:edit')) && (
                  <button 
                    onClick={() => handleRemove(selectedItem)}
                    className="px-4 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded-lg font-medium transition-colors"
                  >
                    彻底删除 (取消采纳)
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );})()}
      
      {/* Pagination */}
      {!loading && totalItems > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-slate-700 mt-6 pt-4 shrink-0">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            共 {totalItems} 条记录
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-200 dark:border-slate-700 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              上一页
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {currentPage} / {totalPages || 1}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 border border-gray-200 dark:border-slate-700 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              下一页
            </button>
          </div>
        </div>
      )}

      {/* Sensitive Config Modal */}
      {configOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-gray-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" /> 全局防污染预警规则设置
              </h2>
              <button onClick={() => setConfigOpen(false)} className="text-gray-400 hover:text-gray-600 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                请设置需要阻截或监控的全局敏感词/黑名单关键词。当抓取到的语料包含这些词汇时，系统将自动标记为<strong className="text-red-500 font-bold mx-1">高危语料</strong>并高亮，隔离使用以防“数据投毒”。
              </p>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">敏感词库 (逗号或换行分隔)</label>
              <textarea 
                className="w-full h-32 px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-gray-900 dark:text-white"
                placeholder="例如：自燃,欺诈,退款,质量差,骗人..."
                value={sensitiveWords}
                onChange={e => setSensitiveWords(e.target.value)}
              />
            </div>
            <div className="p-5 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/80 flex justify-end gap-3">
              <button onClick={() => setConfigOpen(false)} className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">取消</button>
              <button 
                onClick={saveConfig}
                disabled={savingConfig}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50"
              >
                {savingConfig ? '保存中...' : '保存配置并生效'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
